import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  BotBaseStrategySelectorData,
  BotFormType,
  BotStrategySelectorData,
  ChannelValue,
  GridType,
  InitializeBaseStrategyValues,
  MaxPriceDifferenceData,
  MultiValue,
  ParsedDcaStrategyData,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import {
  BotType,
  OrderTypes,
  StrategyStyleType,
  LanguageNameSpaces,
} from "@shared/types";
import { isSymbol } from "@shared/types/utils";
import {
  channelValue,
  DCA_STRATEGY_KEY_TO_TITLE,
  GRID_MAX_PRICE_DIFFERENCE_TEXT,
  MAX_PRICE_DIFFERENCE_TEXT,
} from "@shared/features/bots/BotForm/common/constants";
import {
  getParsedStrategyValue,
  isHedgingRangeStrategyMixed,
  isCustomStrategy,
  validateBaseStrategyData,
} from "@shared/features/bots/BotForm/common/utils";
import {
  getCreateItem,
  getItemByName,
  getItemValidations,
} from "@shared/features/configuration/utils";
import { getConfigurationItem } from "@shared/features/bots/utils";
import { DCA_STRATEGIES_ITEM_NAME } from "@shared/features/configuration/constants";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { findLastNonZeroValue } from "@shared/utils/arrays";
import useFormHelperData from "@shared/features/bots/BotForm/common/hooks/useFormHelperData";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface BotBaseStrategiesProviderProps {
  orderType: OrderTypes.ENTRY | OrderTypes.TAKE_PROFIT;
  singleColumnLayout?: boolean;
}

const BotBaseStrategiesProvider = ({
  orderType,
  singleColumnLayout,
  children,
}: PropsWithChildren<BotBaseStrategiesProviderProps>) => {
  const {
    botGeneralData,
    checkedFields,
    gridsData,
    hedgingGridsData,
    botType,
    botFormType,
    isChannelConfiguration,
    entryStrategy,
    takeProfitStrategy,
    hedgingMode,
    longShort,
    symbolPriceData,
  } = useContext(BotFormContext) || {};
  const [numOrders, setNumOrders] = useState<
    string | undefined | MultiValue | ChannelValue
  >();
  const [numActiveOrders, setNumActiveOrders] = useState<
    string | undefined | MultiValue | ChannelValue
  >();
  const [priceDifference, setPriceDifference] = useState<
    string | undefined | MultiValue | ChannelValue
  >();
  const [priceScale, setPriceScale] = useState<
    string | undefined | MultiValue | ChannelValue
  >();
  const { getHelperData } = useFormHelperData();
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isGridLikeBot = isGridBot || isHedgingBot;
  const activeGridsData = isHedgingBot ? hedgingGridsData : gridsData;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isEntry = orderType === OrderTypes.ENTRY;
  const isPreservingMixedHedgingRangeStrategy =
    isEntry &&
    isHedgingRangeStrategyMixed({
      hedgingMode,
      entryStrategy,
      hedgingGridsData,
    });
  const includeNumOrders = !isChannelConfiguration;
  const includeNumActiveOrders =
    isEntry && !isSignalsBot && !isTradingViewSignalsBot && !isHedgingBot;
  const ignorePriceStrategy = !checkedFields?.has(
    orderType === OrderTypes.ENTRY
      ? ToggleField.ENTRY_PRICE_STRATEGY
      : ToggleField.TAKE_PROFIT_PRICE_STRATEGY
  );
  const strategy = isEntry ? entryStrategy : takeProfitStrategy;
  const parsedStrategy = strategy && !isSymbol(strategy) ? strategy : undefined;

  useEffect(() => {
    if (isCustomStrategy(parsedStrategy)) {
      const numTargets = (
        parsedStrategy.targets && !isSymbol(parsedStrategy.targets)
          ? findLastNonZeroValue(parsedStrategy.targets)
          : 0
      ).toString();
      if (numTargets !== numOrders) {
        setNumOrders(numTargets);
      }
    }
  }, [parsedStrategy]);

  const strategiesItem = getConfigurationItem(
    botGeneralData,
    orderType === OrderTypes.ENTRY ? "Entry Strategy" : "Take-Profit Strategy"
  );
  const dcaStrategiesItem = getItemByName(
    DCA_STRATEGIES_ITEM_NAME,
    strategiesItem
  );
  const createDcaStrategyTemplate = getCreateItem(dcaStrategiesItem);

  const {
    max_number_of_orders,
    max_number_of_active_orders,
    max_price_difference,
    max_price_scale,
  } = getItemValidations(createDcaStrategyTemplate) || {};

  const parsedData: Pick<
    ParsedDcaStrategyData,
    "numOrders" | "numActiveOrders" | "priceDifference" | "priceScale"
  > = useMemo(
    () => ({
      numOrders: getParsedStrategyValue(numOrders),
      numActiveOrders: getParsedStrategyValue(numActiveOrders),
      priceDifference: getParsedStrategyValue(priceDifference),
      priceScale: getParsedStrategyValue(priceScale),
    }),
    [
      numOrders,
      numActiveOrders,
      priceDifference,
      priceScale,
      ignorePriceStrategy,
    ]
  ) as ParsedDcaStrategyData;

  const initializeValues: InitializeBaseStrategyValues = (
    numOrders,
    priceDifference,
    priceScale,
    numberOfActiveOrders
  ) => {
    const getParsedValue = (value?: any) =>
      isSymbol(value) ? value : value?.toString();
    setNumOrders?.(getParsedValue(numOrders));
    setPriceDifference?.(getParsedValue(priceDifference));
    setPriceScale?.(getParsedValue(priceScale));
    setNumActiveOrders?.(getParsedValue(numberOfActiveOrders));
  };

  const currentPrice = symbolPriceData?.data || 0;
  const pricePrecision = symbolPriceData?.precision.price || 0;
  const isBulkEditBots = botFormType === BotFormType.BULK_EDIT;

  const {
    isNumOrdersValid,
    isNumActiveOrdersValid,
    isPriceDifferenceValid,
    isPriceScaleValid,
    pricesValidationData,
  } = useMemo(() => {
    return validateBaseStrategyData(
      orderType,
      parsedData,
      createDcaStrategyTemplate,
      includeNumOrders,
      includeNumActiveOrders,
      ignorePriceStrategy || isPreservingMixedHedgingRangeStrategy,
      longShort,
      currentPrice,
      pricePrecision,
      activeGridsData,
      botType
    );
  }, [
    orderType,
    parsedData,
    createDcaStrategyTemplate,
    includeNumOrders,
    includeNumActiveOrders,
    ignorePriceStrategy,
    isPreservingMixedHedgingRangeStrategy,
    longShort,
    currentPrice,
    pricePrecision,
    activeGridsData,
    botType,
  ]);

  const isChannelPrices =
    parsedStrategy?.price_scale === channelValue ||
    parsedStrategy?.price_difference === channelValue;
  const isChannelRatios =
    (parsedStrategy &&
      "amount_scale" in parsedStrategy &&
      parsedStrategy.amount_scale === channelValue) ||
    parsedStrategy?.strategy_style === channelValue;
  const isMaxNumOrders = isChannelPrices || isChannelRatios;

  const getBaseSelectorData = (
    strategyKey: keyof ParsedDcaStrategyData,
    value: any
  ) => {
    const isMultiValue = isSymbol(value);
    let title = t(DCA_STRATEGY_KEY_TO_TITLE[strategyKey]);
    if (strategyKey === "numOrders" && isMaxNumOrders) {
      title = t("strategy.max_number_of_orders");
    }
    return {
      title,
      value: (isMultiValue ? "" : value) as string | undefined,
      rawValue: value,
      placeholder:
        isMultiValue && value !== channelValue ? t("labels.multi") : undefined,
      helperTooltipData: getHelperData(strategyKey, orderType),
    };
  };

  const numOrdersSelectorData = {
    show: includeNumOrders && !isGridLikeBot,
    ...getBaseSelectorData("numOrders", numOrders),
    onChange: setNumOrders,
    maxValue: max_number_of_orders,
    step: 1,
    minValue: 1,
    showError: !isNumOrdersValid,
    clickWarning:
      !isSymbol(strategy) &&
      strategy?.strategy_style === StrategyStyleType.CUSTOM
        ? `The ${
            isMaxNumOrders ? "Max " : ""
          }Number of Orders parameter can't be modified when using a custom ratio strategy. Please use the '+' or '-' buttons in the ${
            isEntry ? "Entries" : "Take-Profit"
          } Ratios section instead.`
        : undefined,
  };

  const numActiveOrdersSelectorData = {
    ...getBaseSelectorData("numActiveOrders", numActiveOrders),
    onChange: setNumActiveOrders,
    show: includeNumActiveOrders,
    maxValue: max_number_of_active_orders,
    step: 1,
    minValue: 1,
    showError: !isNumActiveOrdersValid,
  };

  let parsedPriceDifference = isSymbol(priceDifference)
    ? undefined
    : priceDifference;
  if (
    parsedPriceDifference &&
    !isSymbol(parsedPriceDifference) &&
    parsedPriceDifference.includes(".")
  ) {
    const splitPriceDifference = parsedPriceDifference.split(".");
    parsedPriceDifference =
      splitPriceDifference[0] + "." + splitPriceDifference[1].slice(0, 2);
  }
  const priceDifferenceSelectorData = {
    ...getBaseSelectorData("priceDifference", priceDifference),
    onChange: setPriceDifference,
    value: parsedPriceDifference,
    maxValue: max_price_difference * 100,
    minValue: 0.01,
    step: 0.1,
    precision: 2,
    suffix: "%",
    showError:
      !isPreservingMixedHedgingRangeStrategy && !isPriceDifferenceValid,
  };

  const priceScaleSelectorData = {
    ...getBaseSelectorData("priceScale", priceScale),
    onChange: setPriceScale,
    maxValue: max_price_scale,
    minValue: 0.001,
    step: 0.1,
    precision: 3,
    showError: !isPriceScaleValid,
    show: !isGridLikeBot || activeGridsData?.gridType === GridType.CUSTOM,
    wait: 500,
  };

  const maxPriceDifferenceData = {
    shouldDisplay:
      !isChannelConfiguration &&
      !isBulkEditBots &&
      !isSymbol(priceDifference) &&
      !isSymbol(priceScale) &&
      !isSymbol(numOrders),
    text: `${t(
      isGridLikeBot ? GRID_MAX_PRICE_DIFFERENCE_TEXT : MAX_PRICE_DIFFERENCE_TEXT
    )}: ${pricesValidationData.maxPriceDifference}% `,
    error: pricesValidationData.maxPriceDifferenceError,
    hasConsecutiveGridsDistanceError:
      pricesValidationData.hasConsecutiveGridsDistanceError,
    helperData: getHelperData("maxPriceDifference"),
  };

  return (
    <BotBaseStrategiesContext.Provider
      value={{
        numOrdersSelectorData,
        numActiveOrdersSelectorData,
        priceDifferenceSelectorData,
        priceScaleSelectorData,
        getBaseSelectorData,
        orderType,
        initializeValues,
        singleColumnLayout,
        maxPriceDifferenceData,
      }}
    >
      {children}
    </BotBaseStrategiesContext.Provider>
  );
};

export default BotBaseStrategiesProvider;

export const BotBaseStrategiesContext = createContext<
  | {
      numOrdersSelectorData: BotStrategySelectorData;
      numActiveOrdersSelectorData: BotStrategySelectorData;
      priceDifferenceSelectorData: BotStrategySelectorData;
      priceScaleSelectorData: BotStrategySelectorData;
      getBaseSelectorData: (
        strategyKey: keyof ParsedDcaStrategyData,
        value: any
      ) => BotBaseStrategySelectorData;
      orderType: OrderTypes.ENTRY | OrderTypes.TAKE_PROFIT;
      initializeValues: InitializeBaseStrategyValues;
      singleColumnLayout?: boolean;
      maxPriceDifferenceData: MaxPriceDifferenceData;
    }
  | undefined
>(undefined);
